import { DateTime } from "luxon";

let displayNames: { of: (tz: string) => string | undefined };

try {
  // This may throw if "timeZone" isn't supported in your Node/ICU version.
  displayNames = new Intl.DisplayNames(["en"], {
    type: "timeZone" as Intl.DisplayNamesOptions["type"],
  });
} catch (error) {
  console.warn(
    "Intl.DisplayNames does not support 'timeZone', fallback to raw timezone string."
  );
  displayNames = { of: (tz: string) => tz };
}

export function isAmpmTimezone(timezone: string): boolean {
  const ampmPrefixes = [
    "America/",
    "Australia/",
    "Pacific/Honolulu",
    "Pacific/Auckland",
    "Asia/Manila",
    "Asia/Kolkata",
  ];
  return ampmPrefixes.some((prefix) => timezone.startsWith(prefix));
}

export function getHourCycle(timezone: string): "h12" | "h23" {
  return isAmpmTimezone(timezone) ? "h12" : "h23";
}

export function getFriendlyTimezoneName(timezone: string): string {
  return displayNames.of(timezone) ?? timezone;
}

export function getFormattedDateTime(
  date: DateTime,
  timezone: string,
  includeSeconds = true
): string {
  const dateOrder = getDateOrderForTimezone(timezone);
  const useAmPm = isAmpmTimezone(timezone);

  const datePart = date
    .setZone(timezone)
    .toFormat(getDateFormatString(dateOrder));
  const timeFormat = useAmPm
    ? includeSeconds
      ? "h:mm:ss a"
      : "h:mm a"
    : includeSeconds
      ? "HH:mm:ss"
      : "HH:mm";

  const timePart = date.setZone(timezone).toFormat(timeFormat);

  return `${datePart}, ${timePart}`;
}

// Returns "DMY", "MDY", or "YMD" based on the timezone
export function getDateOrderForTimezone(
  timezone: string
): "DMY" | "MDY" | "YMD" {
  // US and territories: MDY
  if (
    timezone.startsWith("America/") &&
    (timezone === "America/New_York" ||
      timezone === "America/Chicago" ||
      timezone === "America/Denver" ||
      timezone === "America/Los_Angeles" ||
      timezone === "America/Phoenix" ||
      timezone === "America/Anchorage" ||
      timezone === "America/Adak" ||
      timezone === "America/Indiana/Indianapolis" ||
      timezone === "America/Indiana/Knox" ||
      timezone === "America/Indiana/Marengo" ||
      timezone === "America/Indiana/Petersburg" ||
      timezone === "America/Indiana/Tell_City" ||
      timezone === "America/Indiana/Vevay" ||
      timezone === "America/Indiana/Vincennes" ||
      timezone === "America/Indiana/Winamac" ||
      timezone === "America/Kentucky/Louisville" ||
      timezone === "America/Kentucky/Monticello" ||
      timezone === "America/Detroit" ||
      timezone === "America/Boise" ||
      timezone === "America/Juneau" ||
      timezone === "America/Sitka" ||
      timezone === "America/Metlakatla" ||
      timezone === "America/Nome" ||
      timezone === "America/Yakutat" ||
      timezone === "America/Puerto_Rico" ||
      timezone === "America/Virgin")
  ) {
    return "MDY";
  }

  // East Asia and ISO: YMD
  if (
    timezone.startsWith("Asia/") &&
    (timezone.includes("Tokyo") ||
      timezone.includes("Seoul") ||
      timezone.includes("Pyongyang") ||
      timezone.includes("Shanghai") ||
      timezone.includes("Taipei") ||
      timezone.includes("Ulaanbaatar"))
  ) {
    return "YMD";
  }

  // Hungary, Lithuania, Mongolia, Bhutan: YMD
  if (
    timezone.startsWith("Europe/Budapest") ||
    timezone.startsWith("Europe/Vilnius") ||
    timezone.startsWith("Asia/Ulaanbaatar") ||
    timezone.startsWith("Asia/Thimphu")
  ) {
    return "YMD";
  }

  // Default: DMY (Europe, Latin America, Africa, South Asia, Oceania)
  return "DMY";
}

// Returns a Luxon-compatible date format string for the given order
export function getDateFormatString(dateOrder: "DMY" | "MDY" | "YMD"): string {
  switch (dateOrder) {
    case "DMY":
      return "dd-MM-yyyy"; // Changed from dd/MM/yyyy
    case "MDY":
      return "MM-dd-yyyy"; // Changed from MM/dd/yyyy
    case "YMD":
      return "yyyy-MM-dd"; // Changed from yyyy/MM/dd
    default:
      return "yyyy-MM-dd";
  }
}
